/**
 * Trunnion AI - Schedule Preview Component
 * Shows the next 5 scheduled runs based on a cron expression.
 * Displays date, time, and timezone in a compact list format.
 */

import { useMemo } from 'react';
import { CalendarClock, AlertCircle } from 'lucide-react';
import { getNextRunTimes, validateCron } from './CronExpression';

interface SchedulePreviewProps {
  cron: string;
  timezone: string;
  disabled?: boolean;
}

function formatRunTime(date: Date, timezone: string): { date: string; time: string } {
  const dateStr = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone === 'UTC' ? 'UTC' : timezone,
  });

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone === 'UTC' ? 'UTC' : timezone,
    hour12: true,
  });

  return { date: dateStr, time: timeStr };
}

function getTimezoneAbbreviation(timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone === 'UTC' ? 'UTC' : timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart?.value ?? timezone;
  } catch {
    return timezone;
  }
}

export function SchedulePreview({ cron, timezone, disabled = false }: SchedulePreviewProps) {
  const nextRuns = useMemo(() => {
    if (disabled || !cron) return [];
    return getNextRunTimes(cron, 5);
  }, [cron, disabled]);

  const cronError = useMemo(() => validateCron(cron), [cron]);
  const tzAbbrev = useMemo(() => getTimezoneAbbreviation(timezone), [timezone]);

  if (disabled) {
    return (
      <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900/50 p-4">
        <div className="flex items-center gap-2 text-trunnion-slate-500">
          <CalendarClock className="h-4 w-4" />
          <span className="text-sm">Schedule disabled - no upcoming runs</span>
        </div>
      </div>
    );
  }

  if (cronError) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4">
        <div className="flex items-center gap-2 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{cronError}</span>
        </div>
      </div>
    );
  }

  if (nextRuns.length === 0) {
    return (
      <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900/50 p-4">
        <div className="flex items-center gap-2 text-trunnion-slate-500">
          <CalendarClock className="h-4 w-4" />
          <span className="text-sm">No upcoming runs</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-trunnion-slate-400" />
        <span className="text-xs font-medium text-trunnion-slate-400 uppercase tracking-wider">
          Next {nextRuns.length} Runs
        </span>
      </div>

      <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900/50 divide-y divide-trunnion-slate-700/50">
        {nextRuns.map((run, idx) => {
          const formatted = formatRunTime(run, timezone);
          return (
            <div
              key={idx}
              className="flex items-center justify-between px-3 py-2 text-sm"
            >
              <span className="text-trunnion-slate-300">{formatted.date}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-white">{formatted.time}</span>
                <span className="text-xs text-trunnion-slate-500">{tzAbbrev}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
